/**
 * Armazenamento local da coleção de powers (wishlist + conquistados).
 *
 * Principal: IndexedDB
 * Fallback: memória (sessão) se IndexedDB estiver indisponível
 *
 * Não misturar lógica IDB nos componentes — usar este serviço / Context.
 *
 * Migração futura (conta xat):
 * - Criar ApiPowerCollectionRepository com a mesma interface PowerCollectionRepository
 * - Trocar getPowerCollectionRepository() conforme auth
 * - Opcional: sync IndexedDB ↔ API no login
 */

import {
  midXats,
  type OwnedItem,
  type PowerCollectionInput,
  type PowerCollectionRepository,
  type PowerCollectionStats,
  type WishItem,
} from '@/lib/power-collection-types';

const DB_NAME = 'xat-ajuda-power-collection';
const DB_VERSION = 1;
const STORE_WISHES = 'wishes';
const STORE_OWNED = 'owned';

type AdapterKind = 'indexeddb' | 'memory';

let adapterKind: AdapterKind = 'indexeddb';
let idbUnavailableNotified = false;
let dbPromise: Promise<IDBDatabase> | null = null;

const memoryWishes = new Map<number, WishItem>();
const memoryOwned = new Map<number, OwnedItem>();

export function getPowerCollectionAdapterKind(): AdapterKind {
  return adapterKind;
}

export function wasIdbFallbackActivated(): boolean {
  return adapterKind === 'memory' && idbUnavailableNotified;
}

export function consumeIdbFallbackFlag(): boolean {
  if (!idbUnavailableNotified) return false;
  idbUnavailableNotified = false;
  return true;
}

function nowIso(): string {
  return new Date().toISOString();
}

function toWish(item: PowerCollectionInput): WishItem {
  return {
    powerId: item.powerId,
    name: item.name,
    shortname: item.shortname,
    category: item.category,
    typeLabel: item.typeLabel,
    ownerXatId: item.ownerXatId ?? null,
    syncedAt: item.syncedAt ?? null,
    savedAt: nowIso(),
    savedMinXats: item.minXats,
    savedMaxXats: item.maxXats,
    savedMinDays: item.minDays,
    savedMaxDays: item.maxDays,
  };
}

function toOwned(item: PowerCollectionInput): OwnedItem {
  return {
    powerId: item.powerId,
    name: item.name,
    shortname: item.shortname,
    category: item.category,
    typeLabel: item.typeLabel,
    ownerXatId: item.ownerXatId ?? null,
    syncedAt: item.syncedAt ?? null,
    acquiredAt: nowIso(),
    acquiredMinXats: item.minXats,
    acquiredMaxXats: item.maxXats,
    acquiredMinDays: item.minDays,
    acquiredMaxDays: item.maxDays,
  };
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_WISHES)) {
        db.createObjectStore(STORE_WISHES, { keyPath: 'powerId' });
      }
      if (!db.objectStoreNames.contains(STORE_OWNED)) {
        db.createObjectStore(STORE_OWNED, { keyPath: 'powerId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });

  return dbPromise;
}

function idbReq<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function activateMemoryFallback(): void {
  if (adapterKind === 'memory') return;
  adapterKind = 'memory';
  idbUnavailableNotified = true;
  dbPromise = null;
}

async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>
): Promise<T> {
  try {
    const db = await openDb();
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const result = fn(store);
    const value = result instanceof Promise ? await result : await idbReq(result);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB tx failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB tx aborted'));
    });
    return value;
  } catch (err) {
    activateMemoryFallback();
    throw err;
  }
}

/* ── Memory adapter ─────────────────────────────────────────────── */

const memoryRepo: PowerCollectionRepository = {
  async addWish(item) {
    const wish = toWish(item);
    memoryWishes.set(wish.powerId, wish);
    return wish;
  },
  async removeWish(powerId) {
    memoryWishes.delete(powerId);
  },
  async markOwned(item) {
    memoryWishes.delete(item.powerId);
    const owned = toOwned(item);
    memoryOwned.set(owned.powerId, owned);
    return owned;
  },
  async removeOwned(powerId) {
    memoryOwned.delete(powerId);
  },
  async listWishes() {
    return Array.from(memoryWishes.values()).sort((a, b) =>
      b.savedAt.localeCompare(a.savedAt)
    );
  },
  async listOwned() {
    return Array.from(memoryOwned.values()).sort((a, b) =>
      b.acquiredAt.localeCompare(a.acquiredAt)
    );
  },
  async hasWish(powerId) {
    return memoryWishes.has(powerId);
  },
  async hasOwned(powerId) {
    return memoryOwned.has(powerId);
  },
};

/* ── IndexedDB adapter ──────────────────────────────────────────── */

const idbRepo: PowerCollectionRepository = {
  async addWish(item) {
    const wish = toWish(item);
    await withStore(STORE_WISHES, 'readwrite', (store) => store.put(wish));
    return wish;
  },
  async removeWish(powerId) {
    await withStore(STORE_WISHES, 'readwrite', (store) => store.delete(powerId));
  },
  async markOwned(item) {
    const owned = toOwned(item);
    try {
      const db = await openDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_WISHES, STORE_OWNED], 'readwrite');
        tx.objectStore(STORE_WISHES).delete(item.powerId);
        tx.objectStore(STORE_OWNED).put(owned);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('markOwned failed'));
        tx.onabort = () => reject(tx.error ?? new Error('markOwned aborted'));
      });
    } catch (err) {
      activateMemoryFallback();
      return memoryRepo.markOwned(item);
    }
    return owned;
  },
  async removeOwned(powerId) {
    await withStore(STORE_OWNED, 'readwrite', (store) => store.delete(powerId));
  },
  async listWishes() {
    const rows = await withStore<WishItem[]>(STORE_WISHES, 'readonly', (store) =>
      store.getAll()
    );
    return rows.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  },
  async listOwned() {
    const rows = await withStore<OwnedItem[]>(STORE_OWNED, 'readonly', (store) =>
      store.getAll()
    );
    return rows.sort((a, b) => b.acquiredAt.localeCompare(a.acquiredAt));
  },
  async hasWish(powerId) {
    const row = await withStore<WishItem | undefined>(STORE_WISHES, 'readonly', (store) =>
      store.get(powerId)
    );
    return row != null;
  },
  async hasOwned(powerId) {
    const row = await withStore<OwnedItem | undefined>(STORE_OWNED, 'readonly', (store) =>
      store.get(powerId)
    );
    return row != null;
  },
};

/**
 * Repositório activo. Em falha de IDB, passa a memória de forma transparente.
 * Futuro: se utilizador autenticado → ApiPowerCollectionRepository.
 */
export function getPowerCollectionRepository(): PowerCollectionRepository {
  if (adapterKind === 'memory') return memoryRepo;

  return {
    async addWish(item) {
      try {
        return await idbRepo.addWish(item);
      } catch {
        return memoryRepo.addWish(item);
      }
    },
    async removeWish(powerId) {
      try {
        await idbRepo.removeWish(powerId);
      } catch {
        await memoryRepo.removeWish(powerId);
      }
    },
    async markOwned(item) {
      try {
        return await idbRepo.markOwned(item);
      } catch {
        return memoryRepo.markOwned(item);
      }
    },
    async removeOwned(powerId) {
      try {
        await idbRepo.removeOwned(powerId);
      } catch {
        await memoryRepo.removeOwned(powerId);
      }
    },
    async listWishes() {
      try {
        return await idbRepo.listWishes();
      } catch {
        return memoryRepo.listWishes();
      }
    },
    async listOwned() {
      try {
        return await idbRepo.listOwned();
      } catch {
        return memoryRepo.listOwned();
      }
    },
    async hasWish(powerId) {
      try {
        return await idbRepo.hasWish(powerId);
      } catch {
        return memoryRepo.hasWish(powerId);
      }
    },
    async hasOwned(powerId) {
      try {
        return await idbRepo.hasOwned(powerId);
      } catch {
        return memoryRepo.hasOwned(powerId);
      }
    },
  };
}

export type CurrentPriceLookup = {
  minXats: number;
  maxXats: number;
};

/**
 * Estatísticas da coleção cruzadas com preços actuais do catálogo Fairtrade.
 * Valor estimado = soma das médias (min+max)/2 dos powers na wishlist.
 */
export function computePowerCollectionStats(
  wishes: WishItem[],
  owned: OwnedItem[],
  catalogTotal: number,
  currentPricesById: Map<number, CurrentPriceLookup>
): PowerCollectionStats {
  let estimatedValueXats = 0;
  for (const wish of wishes) {
    const current = currentPricesById.get(wish.powerId);
    if (current) {
      estimatedValueXats += midXats(current.minXats, current.maxXats);
    } else {
      estimatedValueXats += midXats(wish.savedMinXats, wish.savedMaxXats);
    }
  }

  const total = Math.max(0, catalogTotal);
  return {
    wishCount: wishes.length,
    ownedCount: owned.length,
    estimatedValueXats: Math.round(estimatedValueXats),
    catalogTotal: total,
    progress: total > 0 ? owned.length / total : 0,
  };
}

/** Probe cedo — activa fallback se IndexedDB não abrir. */
export async function ensurePowerCollectionStorageReady(): Promise<AdapterKind> {
  if (adapterKind === 'memory') return 'memory';
  try {
    await openDb();
    return 'indexeddb';
  } catch {
    activateMemoryFallback();
    return 'memory';
  }
}
